import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useForm, UseFormSetValue } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import {
  Alert,
  useAbsolutePriceDropDialog,
  useManualPriceDisableAutoPricingDialog,
  useRelativeFloorPriceDropDialog,
  useRelativePriceDropDialog,
  useSuspiciousPriceChangeDialog,
} from 'src/dialogs/AlertWithSuppressionDialog';
import { useManualPriceAdjustFloorOrCeilingDialog } from 'src/dialogs/AlertWithSuppressionDialog/useManualPriceAdjustFloorOrCeilingDialog';
import { useConfirmationDialogSettings } from 'src/hooks/api/useConfirmationDialogSettings';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  validateAutoPricingSettings,
  validateCompListingSettings,
  validateUndercutSettings,
} from 'src/utils/autoPricingUtils';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { isDatePassedHours } from 'src/utils/dateTimeUtils';
import { isSuccess } from 'src/utils/errorUtils';
import {
  getListingDetailsUpdateInput,
  isAbsolutePriceDrop,
  isRelativeFloorPriceDrop,
  isRelativePriceDrop,
  isSuspiciousPriceChange,
} from 'src/utils/inventoryUtils';
import { posFieldFromValues } from 'src/utils/posFieldUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ClientLogRequest,
  Feature,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  ListingDetailsPricingSectionUpdates,
  ListingDetailsUpdateInput,
  ListingPricingSectionPosFieldUpdates,
  LogLevel,
  PosUiActionResult,
} from 'src/WebApiController';

import { ListingWithEvent } from '../ListingTable.types';
import { ListingPricingInput } from './ListingPriceForm.types';

const getUpdateValue = <T>(
  value: T | null,
  defaultValue?: T | null,
  fallback?: T | null
): T | null => {
  if (value === defaultValue) {
    return null;
  }
  return value ?? fallback ?? null;
};

/**
 * Helper method to check if we want to reset the form with the new catalog listing
 * @param listing new catalog listing
 * @param defaultValues form state defaults
 * @returns
 */
const shouldResetForm = (
  logEvent: (logRequest: ClientLogRequest) => void,
  listing: Listing | null | undefined,
  defaultValues?: object & { rowVersion?: number | undefined | null }
): boolean => {
  if (defaultValues == null) {
    return true;
  }
  if (listing) {
    // zero means reset
    if (listing.rowVer === 0) {
      return true;
    }
    // Sanity check to make sure we are not reset to a stale version
    // Ideally this should not happen, log it in case it actually happens
    if (listing.rowVer >= (defaultValues.rowVersion ?? 0)) {
      const priceAndAutoPricingLoaded =
        listing.dataIsLoaded &&
        listing.dataIsLoaded[ListingDetailDataField.Pricing] &&
        listing.dataIsLoaded[ListingDetailDataField.AutoPricing];
      // only reset when both price and auto pricing settings are loaded
      if (priceAndAutoPricingLoaded) {
        return true;
      }
    } else {
      logEvent({
        logLevel: LogLevel.Warning,
        applicationSource: REACT_APP_NAME,
        errorId: null,
        error: `Attempting to update listing form with stale listing version`,
        pageUrl: window.location.href,
        componentStack: 'ListingPriceForm.ts - ResetListingForm',
        timestamp: new Date().toISOString(),
        additionalInfo: {
          listingId: listing.id,
          currentVersion: defaultValues.rowVersion ?? 0,
          newVersion: listing.rowVer,
        },
      });
    }
  }

  return false;
};

export const useListingPriceForm = (rowData: ListingWithEvent | null) => {
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = useMemo(
    () => getUiCurrency(rowData?.listing?.currency),
    [getUiCurrency, rowData?.listing?.currency]
  );
  const hasAllowSeatScoreCompListingMode = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );
  const hasRemovePriceIncreaseRoadbump = useUserHasFeature(
    Feature.RemovePriceIncreaseRoadbump
  );
  const hasSellerEventAlertMutes = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );
  const hasFixUpdateFloorAndCeilingIssuesFeature = useUserHasFeature(
    Feature.FixUpdateFloorAndCeilingIssues
  );

  const viagogoVirtualId = rowData?.event?.virtId;

  // Memoize the default form values to avoid recreating on every render
  const defaultFormValues = useMemo(
    () => ({
      ...getListingDetailsUpdateInput(
        rowData?.listing as ListingDetails,
        uiCurrency,
        undefined,
        null,
        null,
        false,
        null,
        hasAllowSeatScoreCompListingMode
      ),
      isSubmitting: false,
      rowIndex: rowData?.rowIndex || 0,
      currentPageIndex: rowData?.currentPageIndex || 0,
    }),
    [
      rowData?.listing,
      uiCurrency,
      hasAllowSeatScoreCompListingMode,
      rowData?.rowIndex,
      rowData?.currentPageIndex,
    ]
  );

  const createListingPricingInput = useCallback(
    (listing?: Listing | null) => ({
      ...getListingDetailsUpdateInput(
        listing as ListingDetails,
        uiCurrency,
        undefined,
        null,
        null,
        false,
        null,
        hasAllowSeatScoreCompListingMode
      ),
      isSubmitting: false,
      rowIndex: rowData?.rowIndex || 0,
      currentPageIndex: rowData?.currentPageIndex || 0,
    }),
    [
      hasAllowSeatScoreCompListingMode,
      rowData?.currentPageIndex,
      rowData?.rowIndex,
      uiCurrency,
    ]
  );

  const methods = useForm<ListingPricingInput>({
    defaultValues: defaultFormValues,
  });
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    reset,
    formState,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
  } = methods;
  const { showErrorDialog, logEvent } = useClientLogContext();
  const setPriceSettingValues =
    setValue as unknown as UseFormSetValue<ListingDetailsUpdateInput>;
  const catalogDataContext = useCatalogDataContext();
  const { updateItemInEvent, refreshEventForItems } = catalogDataContext;

  const { onUpdateListing } = useEventHubContext();
  const { confirmationDialogSettings } = useConfirmationDialogSettings();

  // Track the listing ID to detect when it actually changes (not just object reference)
  const listingIdRef = useRef(rowData?.listing?.id);
  const listingVersionRef = useRef(rowData?.listing?.rowVer);

  // Nuance - this is necessary so the form picks up whenever the listing changed outside the table
  // like when ListingModal made edits to the data of this listing
  useEffect(() => {
    const currentListingId = rowData?.listing?.id;
    const currentListingVersion = rowData?.listing?.rowVer;

    // Only check for reset if listing ID or version actually changed
    const listingChanged =
      currentListingId !== listingIdRef.current ||
      currentListingVersion !== listingVersionRef.current;

    if (listingChanged) {
      listingIdRef.current = currentListingId;
      listingVersionRef.current = currentListingVersion;

      if (
        shouldResetForm(logEvent, rowData?.listing, formState.defaultValues)
      ) {
        const newForm = createListingPricingInput(rowData?.listing);
        const { rowVersion: _, ...newFormWithoutVersion } = newForm;
        const { rowVersion, ...defaultWithoutVersion } =
          formState.defaultValues ?? {};
        // Only reset when necessary, to persist the user input
        if (!isEqual(newFormWithoutVersion, defaultWithoutVersion)) {
          reset(newForm);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only depend on the actual data that matters - listing ID and version
    rowData?.listing?.id,
    rowData?.listing?.rowVer,
  ]);

  const listPrice = watch('listPrice') ?? -1;
  const allInPrice = watch('allInPrice') ?? -1;
  const isSubmitting = watch('isSubmitting');
  const autoPricingEnabled = watch('autoPricingEnabled');
  const netProceedsFloor = watch('netProceedsFloor');
  const netProceedsCeiling = watch('netProceedsCeiling');
  const isSubmittingPricingSettings = watch('isSubmittingPricingSettings');
  const compListingFloor = watch('compListingFloor');
  const compListingCeiling = watch('compListingCeiling');
  const undercutAbsoluteAmount = watch('undercutAbsoluteAmount');
  const undercutRelativeAmount = watch('undercutRelativeAmount');
  const undercutMode = watch('undercutMode');
  const compListingMode = watch('compListingMode');
  const compListingQuantityScoreAdjustmentEnabled = watch(
    'compListingQuantityScoreAdjustmentEnabled'
  );
  const compListingQuantityScoreAdjustmentOverrideJson = watch(
    'compListingQuantityScoreAdjustmentOverrideJson'
  );
  const compListingOnlyForSameZoneEnabled = watch(
    'compListingOnlyForSameZoneEnabled'
  );
  const compListingOnlyForSelectedSectionsEnabled = watch(
    'compListingOnlyForSelectedSectionsEnabled'
  );
  const compListingExcludeFanInventory = watch(
    'compListingExcludeFanInventory'
  );
  const outlierMode = watch('outlierMode');
  const outlierStandardDeviations = watch('outlierStandardDeviations');
  const outlierKthLowestLimit = watch('outlierKthLowestLimit');
  const outlierKthLowestLimitAbsoluteSpacing = watch(
    'outlierKthLowestLimitAbsoluteSpacing'
  );
  const outlierKthLowestLimitRelativeSpacing = watch(
    'outlierKthLowestLimitRelativeSpacing'
  );
  const circuitBreakerMinCompListingCount = watch(
    'circuitBreakerMinCompListingCount'
  );
  const circuitBreakerMaxDiscountVelocityPercent = watch(
    'circuitBreakerMaxDiscountVelocityPercent'
  );
  const circuitBreakerMaxDiscountVelocityTicksInHours = watch(
    'circuitBreakerMaxDiscountVelocityTicksInHours'
  );

  const submitPriceChange = useCallback(() => {
    if (!rowData?.listing || !activeAccountWebClientConfig.activeAccountId) {
      return;
    }

    const values = getValues();
    const defaultValues = formState.defaultValues;

    tryInvokeApi(
      async () => {
        const client = new ListingClient(activeAccountWebClientConfig);
        const listing = rowData!.listing!;
        let result: PosUiActionResult | null = null;
        if (hasFixUpdateFloorAndCeilingIssuesFeature) {
          const updateInput: ListingPricingSectionPosFieldUpdates = {
            unitNetProceeds: posFieldFromValues(
              values.listPrice,
              defaultValues?.listPrice
            ),
            autoPricingEnabled: posFieldFromValues(
              values.autoPricingEnabled,
              defaultValues?.autoPricingEnabled
            ),
            netProceedsFloor: posFieldFromValues(
              values.netProceedsFloor,
              defaultValues?.netProceedsFloor
            ),
            netProceedsCeiling: posFieldFromValues(
              values.netProceedsCeiling,
              defaultValues?.netProceedsCeiling
            ),
            pricerSellerUserId: posFieldFromValues(
              listing.pricerId,
              defaultValues?.pricerSellerUserId
            ),
          };

          result = await client.updateListingPricingSectionFromListingTable(
            listing.id,
            updateInput
          );
        } else {
          const input: ListingDetailsPricingSectionUpdates = {
            id: listing.id,
            rowVersion: null,
            listPrice: getUpdateValue(
              values.listPrice,
              defaultValues?.listPrice,
              listPrice
            ),
            allInPrice: null, // will be inferred from list price
            buyerUserId: null,
            marketplacePriceUpdates: [],
            netProceedsFloor: getUpdateValue(
              values.netProceedsFloor,
              defaultValues?.netProceedsFloor,
              netProceedsFloor
            ),
            netProceedsCeiling: getUpdateValue(
              values.netProceedsCeiling,
              defaultValues?.netProceedsCeiling,
              netProceedsCeiling
            ),
            autoPricingEnabled: getUpdateValue(
              values.autoPricingEnabled,
              defaultValues?.autoPricingEnabled,
              autoPricingEnabled
            ),
            pricerSellerUserId: listing.pricerId,
            currencyCode: listing.currency,
          };
          result = await client.updateListingPricingSectionByListingId(
            input.id,
            input
          );
        }

        if (isSuccess(result)) {
          // Refresh listing
          const updated = await client.getListingByListingId(
            listing.id,
            listing as ListingDetails,
            [ListingDetailDataField.Pricing]
          );
          if (updated) {
            updateItemInEvent(updated, ActionOutboxEntityType.Listing);
            onUpdateListing(updated.id);
          }

          // Refresh all listings in group if listing is in a group
          if (updated.ltGrpId != null) {
            await refreshEventForItems(
              [updated.id],
              ActionOutboxEntityType.Listing
            );
          }
        }
        return;
      },
      (error) => {},
      () => {
        setValue('isSubmitting', false);
      }
    );
  }, [
    rowData,
    getValues,
    formState.defaultValues,
    activeAccountWebClientConfig,
    hasFixUpdateFloorAndCeilingIssuesFeature,
    listPrice,
    netProceedsFloor,
    netProceedsCeiling,
    autoPricingEnabled,
    updateItemInEvent,
    onUpdateListing,
    refreshEventForItems,
    setValue,
  ]);

  const warningDialog = useBasicDialog();

  const submitPricingSettingsChange = useCallback(
    (listForm: ListingPricingInput) => {
      if (!rowData?.listing) return;

      tryInvokeApi(
        async () => {
          const client = new ListingClient(activeAccountWebClientConfig);
          const listing = rowData!.listing!;
          const input: ListingDetailsAutoPricingSectionUpdates = {
            ...listForm,
            id: listing.id,
            rowVersion: null,
            autoPriceFrequency: null,
            compListingQuantityFilters: null,
            compListingQuantityScoreAdjustmentOverrides: null,
            scoreModel: null,
            autoPricingDefaultQuantityFiltersJson: null,
            venueZoneOverrideId: null,
            venueZoneConfigType: null,
            sectionRowScoreRecommendations: null,
          };

          await client.updateListingAutoPricingSectionByListingId(
            input.id,
            input
          );

          // Refersh listing
          const updatedListing = await client.getListingByListingId(
            listing.id,
            listing as ListingDetails,
            [
              ListingDetailDataField.Pricing, // To refresh pricing related listing fields, i.e. auto pricing enabled
              ListingDetailDataField.AutoPricing,
            ]
          );
          if (updatedListing) {
            updateItemInEvent(updatedListing, ActionOutboxEntityType.Listing);
            reset(createListingPricingInput(updatedListing));
            onUpdateListing(updatedListing.id);
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListing(pricingSettings)',
            error,
            { trackErrorData: listForm }
          );
        },
        () => {
          setValue('isSubmittingPricingSettings', false);
        }
      );
    },
    [
      rowData,
      activeAccountWebClientConfig,
      updateItemInEvent,
      reset,
      createListingPricingInput,
      onUpdateListing,
      showErrorDialog,
      setValue,
    ]
  );

  const suspiciousPriceChangeHookProps = useSuspiciousPriceChangeDialog(
    () => {},
    () => {
      // revert back
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
      setValue('allInPrice', formState.defaultValues?.allInPrice ?? null);
      setValue('isSubmitting', false);
    },
    viagogoVirtualId
  );

  const absolutePriceDropProps = useAbsolutePriceDropDialog(
    () => {},
    () => {
      // revert back
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
      setValue('allInPrice', formState.defaultValues?.allInPrice ?? null);
      setValue('isSubmitting', false);
    },
    viagogoVirtualId
  );

  const relativePriceDropProps = useRelativePriceDropDialog(
    () => {},
    () => {
      // revert back
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
      setValue('allInPrice', formState.defaultValues?.allInPrice ?? null);
      setValue('isSubmitting', false);
    },
    viagogoVirtualId
  );

  const relativeFloorPriceDropProps = useRelativeFloorPriceDropDialog(
    () => {},
    () => {
      // revert back
      setValue('listPrice', formState.defaultValues?.listPrice ?? null);
      setValue('allInPrice', formState.defaultValues?.allInPrice ?? null);
      setValue('isSubmitting', false);
    },
    viagogoVirtualId
  );

  const manualPriceDisableAutoPricingProps =
    useManualPriceDisableAutoPricingDialog(
      () => setValue('autoPricingEnabled', false),
      viagogoVirtualId
    );

  const alignFloorWithProceeds = () => {
    if (listPrice < (netProceedsFloor ?? Number.MIN_VALUE)) {
      setValue('netProceedsFloor', listPrice);
    } else if (listPrice > (netProceedsCeiling ?? Number.MAX_VALUE)) {
      setValue('netProceedsCeiling', listPrice);
    }
  };

  const manualPriceAdjustFloorCeilingProps =
    useManualPriceAdjustFloorOrCeilingDialog(
      listPrice,
      netProceedsFloor,
      netProceedsCeiling,
      () => {
        alignFloorWithProceeds();
      },
      () => {
        if ((listPrice ?? 0) < (netProceedsFloor ?? Number.MIN_VALUE)) {
          setValue('listPrice', netProceedsFloor);
        } else if (
          (listPrice ?? 0) > (netProceedsCeiling ?? Number.MAX_VALUE)
        ) {
          setValue('listPrice', netProceedsCeiling);
        }
      },
      viagogoVirtualId
    );

  const requiredMsg = useContent(ContentId.Required);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const floorMustBeLessThanCeilingError = useContent(
    ContentId.FloorMustBeLessThanCeiling
  );
  const compListingFloorCeilingError = useContent(
    ContentId.CompListingFloorMustLessThanCeiling
  );

  useEffect(() => {
    if (!isSubmittingPricingSettings) {
      return;
    }
    // Input validity?
    if (
      autoPricingEnabled === formState.defaultValues?.autoPricingEnabled &&
      undercutAbsoluteAmount ===
        formState.defaultValues?.undercutAbsoluteAmount &&
      undercutRelativeAmount ===
        formState.defaultValues?.undercutRelativeAmount &&
      undercutMode === formState.defaultValues?.undercutMode &&
      compListingMode === formState.defaultValues?.compListingMode &&
      compListingCeiling === formState.defaultValues?.compListingCeiling &&
      compListingFloor === formState.defaultValues?.compListingFloor &&
      compListingQuantityScoreAdjustmentEnabled ===
        formState.defaultValues?.compListingQuantityScoreAdjustmentEnabled &&
      compListingOnlyForSelectedSectionsEnabled ===
        formState.defaultValues?.compListingOnlyForSelectedSectionsEnabled &&
      compListingQuantityScoreAdjustmentOverrideJson ===
        formState.defaultValues
          ?.compListingQuantityScoreAdjustmentOverrideJson &&
      compListingOnlyForSameZoneEnabled ===
        formState.defaultValues?.compListingOnlyForSameZoneEnabled &&
      compListingExcludeFanInventory ===
        formState.defaultValues?.compListingExcludeFanInventory &&
      outlierMode === formState.defaultValues?.outlierMode &&
      outlierStandardDeviations ===
        formState.defaultValues?.outlierStandardDeviations &&
      outlierKthLowestLimit ===
        formState.defaultValues?.outlierKthLowestLimit &&
      outlierKthLowestLimitAbsoluteSpacing ===
        formState.defaultValues?.outlierKthLowestLimitAbsoluteSpacing &&
      outlierKthLowestLimitRelativeSpacing ===
        formState.defaultValues?.outlierKthLowestLimitRelativeSpacing &&
      circuitBreakerMinCompListingCount ===
        formState.defaultValues?.circuitBreakerMinCompListingCount &&
      circuitBreakerMaxDiscountVelocityPercent ===
        formState.defaultValues?.circuitBreakerMaxDiscountVelocityPercent &&
      circuitBreakerMaxDiscountVelocityTicksInHours ===
        formState.defaultValues?.circuitBreakerMaxDiscountVelocityTicksInHours
    ) {
      // No change, just quit
      setValue('isSubmittingPricingSettings', false);
      return;
    }

    let hasErrors = false;
    const listForm = getValues();

    if (autoPricingEnabled !== formState.defaultValues?.autoPricingEnabled) {
      // Switching on autopricing might set all other values to default, so handle here first
      if (
        !validateAutoPricingSettings(
          clearErrors,
          setError,
          setPriceSettingValues,
          listForm,
          floorMustBeLessThanCeilingError,
          compListingFloorCeilingError,
          requiredMsg
        )
      ) {
        // Reset the field to the original values
        setValue('autoPricingEnabled', rowData?.listing?.isAutoPrc ?? false);
      }
    } else {
      // Only one of the fields should be changed within this branch
      if (
        compListingMode !== formState.defaultValues?.compListingMode ||
        compListingCeiling !== formState.defaultValues?.compListingCeiling ||
        compListingFloor !== formState.defaultValues?.compListingFloor
      ) {
        const fieldEdited =
          compListingCeiling !== formState.defaultValues?.compListingCeiling
            ? 'compListingCeiling'
            : compListingFloor !== formState.defaultValues?.compListingFloor
              ? 'compListingFloor'
              : 'compListingMode';
        if (
          !validateCompListingSettings(
            clearErrors,
            setError,
            setPriceSettingValues,
            listForm,
            compListingFloorCeilingError,
            requiredMsg,
            fieldEdited
          )
        ) {
          hasErrors = true;
        }
      }

      if (
        undercutAbsoluteAmount !==
          formState.defaultValues?.undercutAbsoluteAmount ||
        undercutRelativeAmount !==
          formState.defaultValues?.undercutRelativeAmount ||
        undercutMode !== formState.defaultValues?.undercutMode
      ) {
        const fieldEdited =
          undercutAbsoluteAmount !==
          formState.defaultValues?.undercutAbsoluteAmount
            ? 'undercutAbsoluteAmount'
            : undercutRelativeAmount !==
                formState.defaultValues?.undercutRelativeAmount
              ? 'undercutRelativeAmount'
              : 'undercutMode';
        if (
          !validateUndercutSettings(
            clearErrors,
            setPriceSettingValues,
            listForm,
            fieldEdited
          )
        ) {
          hasErrors = true;
        }
      }
    }

    if (hasErrors) {
      setValue('isSubmittingPricingSettings', false);
      return;
    }

    submitPricingSettingsChange(listForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmittingPricingSettings]);

  useEffect(() => {
    if (!isSubmitting) {
      return;
    }
    if (listPrice >= 0 || allInPrice >= 0 || netProceedsFloor) {
      if (
        listPrice === formState.defaultValues?.listPrice &&
        allInPrice === formState.defaultValues?.allInPrice &&
        netProceedsFloor === formState.defaultValues?.netProceedsFloor
      ) {
        // No change, just quit
        setValue('isSubmitting', false);
        return;
      }

      clearErrors('listPrice');
      clearErrors('netProceedsFloor');

      let hasErrors = false;

      if (listPrice < 0) {
        setError('listPrice', { message: requiredMsg });
        hasErrors = true;
      }
      if (netProceedsFloor && netProceedsFloor < 0) {
        setError('netProceedsFloor', { message: requiredMsg });
        hasErrors = true;
      }

      if (hasErrors) {
        setValue('isSubmitting', false);
        return;
      }

      const newAlerts = [];
      // Check only for updating list price
      if (listPrice > 0 && listPrice !== formState.defaultValues?.listPrice) {
        // FloorCeiling Soft Block
        const isFloorCeilingDialogEnabled =
          confirmationDialogSettings?.settings?.FloorCeilingViolation
            ?.isActive ?? true;

        if (
          listPrice < (netProceedsFloor ?? Number.MIN_VALUE) ||
          listPrice > (netProceedsCeiling ?? Number.MAX_VALUE)
        ) {
          if (
            isDatePassedHours(
              manualPriceAdjustFloorCeilingProps.lastTimeStamp,
              1
            ) &&
            isFloorCeilingDialogEnabled
          ) {
            newAlerts.push(manualPriceAdjustFloorCeilingProps);
          } else {
            // If the last time stamp is not passed, then need to always align the floor
            // and unit net proceeds like the dialog does when you press okay. If the user
            // pressed cancel we do not write the timestamp back to the user setting table
            // so they will run into the dialog again, thus we do not need to cover that case.
            alignFloorWithProceeds();
          }
        }

        // AutoPricing Disable when manual pricing soft block
        const isAutoPricingDisableDialogEnabled =
          confirmationDialogSettings?.settings?.AutoPricingDisable?.isActive ??
          true;

        if (
          isAutoPricingDisableDialogEnabled &&
          autoPricingEnabled &&
          isDatePassedHours(manualPriceDisableAutoPricingProps.lastTimeStamp, 1)
        ) {
          newAlerts.push(manualPriceDisableAutoPricingProps);
        }

        // Suspicious price change soft block (deprecated once hasRemovePriceIncreaseRoadbump is GA'd)
        const isSuspiciousPriceChangeDialogEnabled =
          confirmationDialogSettings?.settings?.SuspiciousPriceChange
            ?.isActive ?? true;

        const showWarning =
          isSuspiciousPriceChange(
            listPrice,
            formState.defaultValues?.listPrice
          ) && !hasRemovePriceIncreaseRoadbump;
        if (
          isSuspiciousPriceChangeDialogEnabled &&
          showWarning &&
          isDatePassedHours(suspiciousPriceChangeHookProps.lastTimeStamp, 1)
        ) {
          newAlerts.push(suspiciousPriceChangeHookProps);
        }

        // Only check new price drop dialogs if user has both feature flags
        if (hasSellerEventAlertMutes && hasRemovePriceIncreaseRoadbump) {
          // Absolute price drop soft block
          const isAbsolutePriceDropEnabled =
            confirmationDialogSettings?.settings?.AbsolutePriceDrop?.isActive ??
            true;
          const absoluteThreshold =
            confirmationDialogSettings?.settings?.AbsolutePriceDrop
              ?.absoluteThreshold ?? Number.MAX_VALUE;

          if (
            isAbsolutePriceDropEnabled &&
            isAbsolutePriceDrop(
              listPrice,
              formState.defaultValues?.listPrice,
              absoluteThreshold
            ) &&
            isDatePassedHours(absolutePriceDropProps.lastTimeStamp, 1)
          ) {
            newAlerts.push(absolutePriceDropProps);
          }

          // Relative price drop soft block
          const isRelativePriceDropEnabled =
            confirmationDialogSettings?.settings?.RelativePriceDrop?.isActive ??
            true;
          const relativeThreshold =
            confirmationDialogSettings?.settings?.RelativePriceDrop
              ?.relativeThreshold ?? 25;

          if (
            isRelativePriceDropEnabled &&
            isRelativePriceDrop(
              listPrice,
              formState.defaultValues?.listPrice,
              relativeThreshold
            ) &&
            isDatePassedHours(relativePriceDropProps.lastTimeStamp, 1)
          ) {
            newAlerts.push(relativePriceDropProps);
          }

          // Floor relative to unit cost price drop soft block
          const isRelativeFloorPriceDropEnabled =
            confirmationDialogSettings?.settings?.RelativeFloorPriceDrop
              ?.isActive ?? true;
          const unitCost = rowData?.listing?.unitCst;
          if (
            isRelativeFloorPriceDropEnabled &&
            isRelativeFloorPriceDrop(listPrice, unitCost?.amt ?? null) &&
            isDatePassedHours(relativeFloorPriceDropProps.lastTimeStamp, 1)
          ) {
            newAlerts.push(relativeFloorPriceDropProps);
          }
        }
      }

      if (newAlerts.length) {
        setAlerts(newAlerts);
        warningDialog.launchDialog();
      } else {
        submitPriceChange();
      }
    } else {
      setValue('isSubmitting', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  const onCancelSuspressionDialog = useCallback(() => {
    // Revert all values to their defaults
    setValue('listPrice', formState.defaultValues?.listPrice ?? null);
    setValue('allInPrice', formState.defaultValues?.allInPrice ?? null);
    setValue(
      'netProceedsFloor',
      formState.defaultValues?.netProceedsFloor ?? null
    );
    setValue(
      'netProceedsCeiling',
      formState.defaultValues?.netProceedsCeiling ?? null
    );
    setValue('isSubmitting', false);
    warningDialog.closeDialog();
  }, [formState.defaultValues, warningDialog, setValue]);

  return {
    onCancelSuspressionDialog,
    submitPriceChange,
    warningDialog,
    alerts,
    viagogoVirtualId,
    methods,
  };
};
